'use client';

import { AlertCircle, CheckCircle2, Info, type LucideIcon, X } from 'lucide-react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { type ToastTone, useToastStore } from '@/stores/toast';

/** Auto-dismiss duration (ms) — shared by the Radix timer and the progress bar. */
const DURATION_MS = 5000;

interface ToneStyle {
  /** Accent bar + progress colour. */
  bar: string;
  /** Icon chip background + foreground. */
  chip: string;
  icon: LucideIcon;
  /** i18n key for the default title when none is provided. */
  titleKey: string;
}

const TONE: Record<ToastTone, ToneStyle> = {
  default: {
    bar: 'bg-ted-sky',
    chip: 'bg-ted-sky-soft text-ted-sky-deep',
    icon: Info,
    titleKey: 'common.toast.info',
  },
  success: {
    bar: 'bg-ted-mint',
    chip: 'bg-ted-mint-soft text-ted-mint-deep',
    icon: CheckCircle2,
    titleKey: 'common.toast.success',
  },
  error: {
    bar: 'bg-ted-coral',
    chip: 'bg-ted-coral-soft text-ted-coral-deep',
    icon: AlertCircle,
    titleKey: 'common.toast.error',
  },
};

/** Global toast viewport; reads the toast store. Mount once near the app root. */
export function Toaster() {
  const { t } = useTranslation();
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);

  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={DURATION_MS}>
      {toasts.map((item) => {
        const tone = TONE[item.tone];
        const Icon = tone.icon;
        return (
          <ToastPrimitive.Root
            key={item.id}
            onOpenChange={(open) => {
              if (!open) dismiss(item.id);
            }}
            className={cn(
              'group pointer-events-auto relative flex items-start gap-3 rounded-card border border-border bg-card px-4 py-3 pb-4 shadow-ted-lift',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full data-[swipe=end]:animate-out data-[swipe=end]:fade-out-80',
            )}
          >
            <span
              className={cn(
                'mt-0.5 grid size-7 shrink-0 place-items-center rounded-full',
                tone.chip,
              )}
            >
              <Icon className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <ToastPrimitive.Title className="text-sm font-bold tracking-tight">
                {item.title ?? t(tone.titleKey)}
              </ToastPrimitive.Title>
              <ToastPrimitive.Description className="text-[13px] leading-snug text-muted-foreground">
                {item.message}
              </ToastPrimitive.Description>
              {item.action && (
                <ToastPrimitive.Action
                  altText={item.action.label}
                  onClick={() => void item.action?.onClick()}
                  className="mt-2 inline-flex min-h-11 items-center rounded-lg border border-current px-3 text-xs font-bold text-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
                >
                  {item.action.label}
                </ToastPrimitive.Action>
              )}
            </div>
            <ToastPrimitive.Close
              aria-label={t('common.close')}
              className="shrink-0 text-muted-foreground opacity-60 transition-opacity hover:opacity-100"
            >
              <X className="size-4" />
            </ToastPrimitive.Close>
            <span
              aria-hidden
              style={{ '--toast-duration': `${DURATION_MS}ms` } as CSSProperties}
              className={cn(
                'absolute right-3 bottom-1.5 left-3 h-1 origin-left rounded-full animate-[toast-progress_var(--toast-duration)_linear_forwards] group-hover:paused motion-reduce:hidden',
                tone.bar,
              )}
            />
          </ToastPrimitive.Root>
        );
      })}
      <ToastPrimitive.Viewport className="fixed right-4 bottom-4 z-100 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none" />
    </ToastPrimitive.Provider>
  );
}
